import clsx from "clsx";
import {
    DeepRequired,
    FieldErrors,
    FieldErrorsImpl,
    FieldName,
    FieldValues,
    Path,
    UnPackAsyncDefaultValues,
    UseFormRegister,
    UseFormSetFocus,
} from "react-hook-form";
import { FieldValuesFromFieldErrors } from "@hookform/error-message";
import { TextboxInput } from "components/fields/items/TextboxInput";
import { ErrorMessageOrHelperText } from "components/fields/items/ErrorMessageOrHelperText";
import { ChangeEvent, FocusEvent, ReactElement, ReactNode } from "react";
import { AdditionalInfoButton } from "components/AdditionalInfoButton";
import { usePasswordToggle } from "hooks/usePasswordToggle";

export function Textbox<T extends FieldValues>(props: {
    register: UseFormRegister<T>;
    name: Path<UnPackAsyncDefaultValues<T>> &
        FieldName<FieldValuesFromFieldErrors<Partial<FieldErrorsImpl<DeepRequired<UnPackAsyncDefaultValues<T>>>>>>;
    label?: string;
    placeholder?: string;
    type?: string;
    min?: number;
    max?: number;
    step?: number;
    required?: boolean;
    maxLength?: number;
    disabled?: boolean;
    autoFocus?: UseFormSetFocus<T>;
    hideLabel?: boolean;
    autoComplete?: string;
    pattern?: string;
    errors: FieldErrors<T>;
    helperText?: ReactNode;
    additionalInfo?: ReactElement;
    inline?: boolean;
    id?: string;
    textArea?: boolean;
    rows?: number;
    className?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}) {
    const {
        name,
        max,
        min,
        step,
        maxLength,
        register,
        placeholder,
        disabled,
        autoFocus,
        required,
        pattern,
        hideLabel,
        label = name,
        type = "text",
        errors,
        helperText,
        inline,
        additionalInfo,
        autoComplete,
        id,
        textArea,
        rows,
        className,
        onChange,
        onBlur,
    } = props;

    const { inputType, toggleButton } = usePasswordToggle(type);

    const inputId = id ?? name;

    return (
        <div
            className={clsx(
                "d-flex gap-2",
                inline ? "align-items-lg-center w-100 flex-column flex-lg-row" : "flex-column",
            )}
        >
            <div className="d-flex">
                <label
                    htmlFor={inputId}
                    className={clsx(
                        hideLabel ? "visually-hidden" : "d-flex form-label align-items-between gap-2 flex-row fw-bold",
                        required && "required",
                        inline && "text-nowrap",
                    )}
                >
                    {label}
                </label>

                {additionalInfo && <AdditionalInfoButton additionalInfo={additionalInfo} />}
            </div>

            <div className={clsx("btn-group", inline && "w-100")}>
                <TextboxInput
                    type={inputType}
                    name={name}
                    register={register}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step}
                    pattern={pattern}
                    required={required}
                    maxLength={maxLength}
                    disabled={disabled}
                    autoComplete={autoComplete}
                    id={inputId}
                    textArea={textArea}
                    rows={rows}
                    className={className}
                    autoFocus={autoFocus}
                    onChange={onChange}
                    onBlur={onBlur}
                />

                {toggleButton}
            </div>

            <ErrorMessageOrHelperText errors={errors} name={name} helperText={helperText} />
        </div>
    );
}
